import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import axios from 'axios';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { ENDPOINTS } from '@/config/endpoints';
import { useRouter } from 'next/router';
import { pushCleverTapEvent } from '../shared/pushCleverTapEvent';

const CredlixremitForm = ({ data }) => {
  const router = useRouter();
  const [thankyouRes, setThankyouRes] = useState(false);
  const [errMSG, setErrMSG] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const formDataConfig = data?.formData || {};
  const labels = formDataConfig.labels || {};
  const placeholders = formDataConfig.placeholders || {};
  const messages = formDataConfig.validationMessages || {};

  const schema = yup.object({
    name: yup
      .string()
      .required(messages.nameRequired),
    receivables: yup
      .string()
      .required(messages.receivablesRequired),
    contact: yup
      .string()
      .required(messages.contactRequired)
      .matches(/^[0-9]{10}$/, messages.contactInvalid),
    email: yup
      .string()
      .required(messages.emailRequired)
      .email(messages.emailInvalid),
    company: yup
      .string()
      .required(messages.companyRequired),
    agree: yup
      .boolean()
      .oneOf([true], messages.agreeRequired),
  });

  const receivablesOptions = formDataConfig.receivablesOptions;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      receivables: receivablesOptions?.[0]?.value,
      contact: '',
      email: '',
      company: '',
      agree: true,
    },
  });

  const handleCaptchaSubmit = useCallback(
    (values) => {
      if (!executeRecaptcha) {
        onFormSubmit(values, '');
        return;
      }
      executeRecaptcha('inrxpressFormSubmit').then((gReCaptchaToken) => {
        onFormSubmit(values, gReCaptchaToken);
      });
    },
    [executeRecaptcha]
  );

  async function onFormSubmit(values, gReCaptchaToken) {
    const body = {
      formType: formDataConfig.formType,
      name: values.name,
      email: values.email,
      phoneNumber: values.contact,
      company: values.company,
      turnover: values.receivables,
      sourceUrl: window.location.href,
    };

    let config = {
      method: 'post',
      url: ENDPOINTS.CONTACT_US_V1,
      headers: {
        'Content-Type': 'application/json',
        'g-recaptcha-response': gReCaptchaToken,
      },
      data: body,
    };

    try {
      const response = await axios(config);
      if (response.status === 200) {
        pushCleverTapEvent("Credlixremit", { "PageName": 'Credlixremit_Page', ...body });
        setThankyouRes(true);
        gtag('event', 'credlixremit', {
          'event_category': 'Submit button',
          'event_label': 'Submit button'
        });
        setTimeout(() => {
          setThankyouRes(false);
          router.push('/thankyou-response');
        }, 3000);
        reset();
      }
    } catch (err) {
      setErrMSG(true);
      console.log('error', err);
    }
  }

  return (
    <div className="bg-white rounded-2xl p-3 sm:p-2 shadow-xl border border-bordercolor flex flex-col">
      {(data?.formTitle || formDataConfig.title) && (
        <h3 className="text-[#072F5F] font-semibold text-lg sm:text-base mb-0.5">
          {data?.formTitle || formDataConfig.title}
        </h3>
      )}
      {(data?.formSubtitle || formDataConfig.subTitle) && (
        <p className="text-[#6F6F6F] text-xs  mb-4 font-medium">
          {data?.formSubtitle || formDataConfig.subTitle}
        </p>
      )}

      <form onSubmit={handleSubmit(handleCaptchaSubmit)} className="flex flex-col">
        {/* Row 1: Your Name (Full Width) */}
        <div className="relative w-full">
          <label className="absolute -top-2.5 left-3 bg-white px-1 text-[11px] font-medium text-[#475569] z-10">
            {labels.name}
          </label>
          <input
            type="text"
            placeholder={placeholders.name}
            {...register('name')}
            className={`w-full h-11 px-3 border ${
              errors.name ? 'border-red-500' : 'border-[#3B82F6]'
            } rounded-lg text-sm text-[#0F172A] font-medium bg-white outline-none focus:ring-1 focus:ring-blue-500`}
          />
          {errors.name && (
            <p className="text-red-500 text-[11px] mt-0.5">{errors.name.message}</p>
          )}
        </div>

        {/* Row 2: Monthly export receivables & Contact */}
        <div className="flex gap-1 sm:flex-col mt-4">
          <div className="relative w-1/2 sm:w-full">
            <label className="absolute -top-2.5 left-3 bg-white px-1 text-[11px] font-medium text-[#475569] z-10">
              {labels.receivables}
            </label>
            <select
              {...register('receivables')}
              className={`w-full h-11 px-3 border ${
                errors.receivables ? 'border-red-500' : 'border-[#3B82F6]'
              } rounded-lg text-sm text-[#0F172A] font-medium bg-white outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer`}
            >
              {receivablesOptions?.map((opt, i) => (
                <option key={i} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {errors.receivables && (
              <p className="text-red-500 text-[11px] mt-0.5">
                {errors.receivables.message}
              </p>
            )}
          </div>

          <div className="relative w-1/2 sm:w-full sm:mt-2">
            <label className="absolute -top-2.5 left-3 bg-white px-1 text-[11px] font-medium text-[#475569] z-10">
              {labels.contact}
            </label>
            <div
              className={`flex h-11 border ${
                errors.contact ? 'border-red-500' : 'border-[#3B82F6]'
              } rounded-lg overflow-hidden items-center px-2 bg-white`}
            >
              <span className="text-sm font-semibold text-[#0F172A] pr-1 border-r border-gray-200 flex items-center gap-1">
                +91
              </span>
              <input
                type="tel"
                placeholder={placeholders.contact}
                maxLength={10}
                {...register('contact')}
                className="w-full h-full px-2 text-sm text-[#0F172A] font-medium bg-white outline-none"
              />
            </div>
            {errors.contact && (
              <p className="text-red-500 text-[11px] mt-0.5">
                {errors.contact.message}
              </p>
            )}
          </div>
        </div>

        {/* Row 3: Work Email & Company Name */}
        <div className="flex gap-1 sm:flex-col mt-4">
          <div className="relative w-1/2 sm:w-full">
            <label className="absolute -top-2.5 left-3 bg-white px-1 text-[11px] font-medium text-[#475569] z-10">
              {labels.email}
            </label>
            <input
              type="email"
              placeholder={placeholders.email}
              {...register('email')}
              className={`w-full h-11 px-3 border ${
                errors.email ? 'border-red-500' : 'border-[#3B82F6]'
              } rounded-lg text-sm text-[#0F172A] font-medium bg-white outline-none focus:ring-1 focus:ring-blue-500`}
            />
            {errors.email && (
              <p className="text-red-500 text-[11px] mt-0.5">
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="relative w-1/2 sm:w-full sm:mt-2">
            <label className="absolute -top-2.5 left-3 bg-white px-1 text-[11px] font-medium text-[#475569] z-10">
              {labels.company}
            </label>
            <input
              type="text"
              placeholder={placeholders.company}
              {...register('company')}
              className={`w-full h-11 px-3 border ${
                errors.company ? 'border-red-500' : 'border-[#3B82F6]'
              } rounded-lg text-sm text-[#0F172A] font-medium bg-white outline-none focus:ring-1 focus:ring-blue-500`}
            />
            {errors.company && (
              <p className="text-red-500 text-[11px] mt-0.5">
                {errors.company.message}
              </p>
            )}
          </div>
        </div>

        {/* Checkbox */}
        <div className="flex flex-col mt-2">
          <div className="flex items-start gap-1">
            <input
              type="checkbox"
              id="agree"
              {...register('agree')}
              className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
            />
            <label
              htmlFor="agree"
              className="text-xs text-[#6F6F6F] leading-tight cursor-pointer"
            >
              {labels.agree}
            </label>
          </div>
          {errors.agree && (
            <p className="text-red-500 text-[11px] mt-0.5">
              {errors.agree.message}
            </p>
          )}
        </div>
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-gradient-to-r from-[#022B5C] to-[#0052CC] hover:from-[#0052CC] hover:to-[#022B5C] transition-all duration-300 text-white font-medium text-sm py-3.5 rounded-full shadow-md mt-1"
        >
          {formDataConfig.buttonText}
        </button>

        {thankyouRes && (
          <p className="text-green text-xs font-semibold text-center mt-2">
            {formDataConfig.successMessage}
          </p>
        )}

        {errMSG && (
          <p className="text-red-600 text-xs font-semibold text-center mt-2">
            There was an error submitting your request. Please try again.
          </p>
        )}

        <p className="text-[10px] text-[#9CA3AF] text-center mt-2">
          {formDataConfig.footnote}
        </p>
      </form>
    </div>
  );
};

export default CredlixremitForm;
